/*
  Instant variant image (snippets/sm-variant-thumbs.liquid). The configurators' updateMedia call
  window.smVariantThumb(root, mediaId) as soon as a variant is chosen; the main image is swapped
  from a preloaded <template> instead of waiting for product-info.js to re-render the section.
*/
(() => {
  if (window.smVariantThumb) return;

  const loads = new Map(); // template key -> { image, ready } (ready settles once decoded)

  const request = (source, priority) => {
    const image = new Image();
    image.fetchPriority = priority;
    if (source.sizes) image.sizes = source.sizes;
    if (source.getAttribute('srcset')) image.srcset = source.getAttribute('srcset');
    image.src = source.getAttribute('src');
    return image;
  };

  const preload = (template, priority = 'low') => {
    const key = template.dataset.smVariantThumb;
    const source = template.content.querySelector('img');
    let entry = loads.get(key);
    if (!entry) {
      entry = { image: null, ready: Promise.resolve(), priority: null };
      loads.set(key, entry);
    }
    if (!source || entry.priority === 'high' || entry.priority === priority) return entry;
    if (entry.image && entry.image.complete && entry.image.naturalWidth) return entry;
    /* A second, high-priority request for the same URL joins the one already in flight. */
    entry.image = request(source, priority);
    entry.priority = priority;
    entry.ready = entry.image.decode().catch(() => {});
    return entry;
  };

  const swap = (root, template, key) => {
    const target = root.querySelector('.product-main-thumb-img');
    if (!target || target.dataset.mediaId === key) return;
    target.replaceWith(template.content.firstElementChild.cloneNode(true));
  };

  /* The configurator calls this several times per click (updateAll re-runs), so a repeat call
     for the image already on its way is ignored rather than restarting the wait. */
  window.smVariantThumb = async (root, mediaId) => {
    if (!root || !mediaId) return;
    const key = `${root.dataset.section}-${mediaId}`;
    if (root.smThumbWanted === key) return;
    const template = root.querySelector(`template[data-sm-variant-thumb="${key}"]`);
    const current = root.querySelector('.product-main-thumb-img');
    if (!template || !current) return;
    root.smThumbWanted = key;
    if (current.dataset.mediaId === key) return;

    /* A swap product-info.js already has in flight is for an older choice: retire it. */
    root.mainThumbToken = (root.mainThumbToken || 0) + 1;

    const entry = preload(template, 'high');
    if (!entry.image || (entry.image.complete && entry.image.naturalWidth)) {
      swap(root, template, key);
      return;
    }
    /* Not downloaded yet (a click straight after page load): keep the current image up until the
       new one is ready rather than swapping to a blank frame; give up waiting after 2.5s. */
    await Promise.race([entry.ready, new Promise((resolve) => setTimeout(resolve, 2500))]);
    if (root.smThumbWanted === key) swap(root, template, key);
  };

  const templates = () => document.querySelectorAll('template[data-sm-variant-thumb]');

  /* Start downloading straight away at low priority (this script is deferred, so the page's own
     main image is already requested); it no longer waits for the page's load event, which the
     app scripts can hold back for several seconds. */
  templates().forEach((template) => preload(template));

  /* As soon as the shopper reaches for the options, move the rest to the front of the queue. */
  const hurry = (event) => {
    if (!event.target.closest || !event.target.closest('[data-sm-smoker-size], [data-sm-power-source]')) return;
    templates().forEach((template) => preload(template, 'high'));
    ['pointerover', 'touchstart', 'focusin'].forEach((type) => document.removeEventListener(type, hurry, true));
  };
  ['pointerover', 'touchstart', 'focusin'].forEach((type) => document.addEventListener(type, hurry, { capture: true, passive: true }));
})();
